/**
 * Created by piotr.pozniak@thebeaverhead.com on 06/02/2019.
 *
 * Untitled UI restyle — no design handoff exists for this page (internal
 * superadmin tool, not customer-facing); styled to match the established
 * OrganizationMembersPage conventions. Name/email filtering, column sort
 * and route-based pagination are preserved from the original.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useHistory, useRouteMatch } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import Card from "../theme/untitled/Card/Card";
import Pagination from "../theme/untitled/Pagination/Pagination";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import LoadingIndicator from "../../calendar/components/LoadingIndicator";
import UsersToolbar from "./UsersPage/UsersToolbar";
import UsersTable from "./UsersPage/UsersTable";
import EditModal from "./UsersPage/EditModal";
import DeleteUserDialog from "./UsersPage/DeleteUserDialog";
import UserDeletedToast from "./UsersPage/UserDeletedToast";
import { useAutologinStore } from "../../../hooks/redux/autologin";
import { store } from "../store";
import { useUserStore } from "../../../hooks/redux/user";
import { useUsersStore } from "../../../hooks/redux/users";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import { useCalendarsStore } from "../../../hooks/redux/calendars";
import { useDomainsStore } from "../../../hooks/redux/domains";
import { useAppTagStore } from "../../../hooks/redux/appTags";
import styles from "./UsersPage/UsersPage.module.scss";

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — same split as
// OrganizationMembersPage.
const UsersPageContent = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const showToast = useToast();

  const { user, editUser } = useUserStore();
  const { users, fetchUsers, deleteUser } = useUsersStore();
  const { autologinUser } = useAutologinStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { appTags, refreshTags } = useAppTagStore();
  const { integrations } = useIntegrationsStore();
  const { calendars } = useCalendarsStore();
  const { domains } = useDomainsStore();

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search]
  );

  const [order, setOrder] = useState({
    field: queryParams.get("sort"),
    direction: queryParams.get("direction"),
  });
  const [nameDraft, setNameDraft] = useState(users.filters.name || "");
  const [emailDraft, setEmailDraft] = useState(users.filters.email || "");
  const [editUserUuid, setEditUserUuid] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletedUserName, setDeletedUserName] = useState(null);

  // Dispatched thunks here always resolve (never reject) — this is how we
  // read the real post-await error/success state, not a stale render-start
  // closure. Same pattern as OrganizationMembersPage.
  const usersRef = useRef(users);
  usersRef.current = users;

  const refreshUsersList = useCallback(() => {
    const page = match.params.page;

    fetchUsers(
      page ? page * 1 : 1,
      50,
      users.filters.name,
      users.filters.email,
      queryParams.get("sort"),
      queryParams.get("direction")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.page, window.location.search]);

  useEffect(() => {
    refreshUsersList();
  }, [match.params.page, window.location.search]);

  const onPaginationPageChange = useCallback(
    (page) => {
      let url = `/users/${page}`;

      if (order.field) {
        url += `?sort=${order.field}`;

        if (order.direction) {
          url += `&direction=${order.direction}`;
        }
      }

      history.push(url);
      window.scrollTo({ top: 0 });
    },
    [order, history]
  );

  useEffect(() => {
    onPaginationPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.field, order.direction]);

  // Keeps the filter inputs in sync with whatever was actually applied
  // (initial URL load, Search, Clear, or a sort/page change that re-fetches
  // with the last-applied filters).
  useEffect(() => {
    setNameDraft(users.filters.name || "");
    setEmailDraft(users.filters.email || "");
  }, [users.filters.name, users.filters.email]);

  useEffect(() => {
    if (users.updateSuccess) {
      setEditUserUuid(null);
    }
  }, [users.updateSuccess]);

  useEffect(() => {
    if (appTags.refreshTagsSuccess) {
      showToast({ variant: "success", message: "Intercom tags refreshed." });
    }
  }, [appTags.refreshTagsSuccess, showToast]);

  const editUserTarget = useMemo(
    () => users.collection.find((i) => i.uuid === editUserUuid) || null,
    [users.collection, editUserUuid]
  );

  const loading = users.fetch || users.delete || appTags.refreshTags;

  const onAutoLogin = useCallback(
    async (autologinAsUser) => {
      await autologinUser(
        autologinAsUser.email,
        user,
        {
          model: currentOrganization.model,
          subscription: currentOrganization.subscription,
        },
        {
          integrations: integrations.collection,
          domains: domains.collection,
          calendars: calendars.collection,
        }
      );

      if (store.getState().autologin.loginSuccess) {
        history.push("/dashboard");
      }
    },
    [
      autologinUser,
      user,
      currentOrganization,
      integrations.collection,
      domains.collection,
      calendars.collection,
      history,
    ]
  );

  const onRefreshIntercomTags = useCallback(
    (targetUser) => refreshTags("contact", targetUser.uuid),
    [refreshTags]
  );

  const onShowEditModal = useCallback(
    (targetUser) => setEditUserUuid(targetUser.uuid),
    []
  );

  const onCloseEditModal = useCallback(() => setEditUserUuid(null), []);

  const onUserEdit = useCallback(
    (uuid, userData) => editUser(uuid, userData),
    [editUser]
  );

  const onOpenDelete = useCallback(
    (targetUser) => setDeleteTarget(targetUser),
    []
  );
  const onCloseDelete = useCallback(() => setDeleteTarget(null), []);

  const onConfirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }

    await deleteUser(deleteTarget.uuid);

    const latest = usersRef.current;
    if (!latest.deleteError) {
      setDeletedUserName(deleteTarget.name);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteUser]);

  const onCloseDeletedToast = useCallback(() => setDeletedUserName(null), []);

  const onChangeOrder = useCallback((field, currentDirection) => {
    const directionsMapping = { null: "asc", asc: "desc", desc: null };

    setOrder((prev) => ({
      field,
      direction:
        prev.field === field
          ? directionsMapping[String(currentDirection)]
          : "asc",
    }));
  }, []);

  const onNameDraftChange = useCallback(
    (e) => setNameDraft(e.target.value),
    []
  );
  const onEmailDraftChange = useCallback(
    (e) => setEmailDraft(e.target.value),
    []
  );

  const isFiltered = !!(users.filters.name || users.filters.email);

  const onSubmitFilter = useCallback(
    (e) => {
      e.preventDefault();
      fetchUsers(1, 50, nameDraft, emailDraft);
    },
    [fetchUsers, nameDraft, emailDraft]
  );

  const onClearFilter = useCallback(() => {
    setNameDraft("");
    setEmailDraft("");
    fetchUsers(1, 50, "", "");
  }, [fetchUsers]);

  const totalPages = Math.ceil(users.total / users.ipp) || 0;

  const userCountLabel = `${users.total} ${users.total === 1 ? "user" : "users"}`;

  const tableContent = users.collection.length ? (
    <UsersTable
      users={users.collection}
      orderField={order.field}
      orderDirection={order.direction}
      onOrder={onChangeOrder}
      rowsDisabled={loading}
      onDelete={onOpenDelete}
      onAutoLogin={onAutoLogin}
      onRefreshIntercomTags={onRefreshIntercomTags}
      onShowEditModal={onShowEditModal}
    />
  ) : (
    <div className={styles.empty}>
      <p className={styles.emptyText}>
        {isFiltered ? "No users match these filters." : "No users yet."}
      </p>
    </div>
  );

  const paginationEl =
    totalPages > 1 ? (
      <Pagination page={users.page} totalPages={totalPages} onPageChange={onPaginationPageChange} />
    ) : null;

  const loadingIndicator = loading ? <LoadingIndicator /> : null;

  const deletedToast = deletedUserName ? (
    <UserDeletedToast name={deletedUserName} onDismiss={onCloseDeletedToast} />
  ) : null;

  return (
    <PageTemplate sidebarId="users" title="Users">
      <Card>
        <UsersToolbar
          name={nameDraft}
          email={emailDraft}
          onNameChange={onNameDraftChange}
          onEmailChange={onEmailDraftChange}
          onSubmit={onSubmitFilter}
          onClear={onClearFilter}
          disabled={loading}
          isFiltered={isFiltered}
        />

        {tableContent}

        <Card.Footer>
          <span className={styles.footerCount}>{userCountLabel}</span>
          {paginationEl}
          {loadingIndicator}
        </Card.Footer>
      </Card>

      <EditModal
        user={editUserTarget}
        users={users}
        onEdit={onUserEdit}
        onClose={onCloseEditModal}
      />

      <DeleteUserDialog
        user={deleteTarget}
        busy={!!deleteTarget && users.delete}
        error={deleteTarget ? users.deleteError : null}
        onConfirm={onConfirmDelete}
        onCancel={onCloseDelete}
      />

      {deletedToast}
    </PageTemplate>
  );
};

const UsersPage = () => (
  <ToastProvider>
    <UsersPageContent />
  </ToastProvider>
);

export default UsersPage;
